import { Button } from '@fsd/ui';
import { CloseCircle, TrashBinTrash } from '@solar-icons/react';
import { useEffect, useRef } from 'react';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  productName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
}

export function DeleteConfirmModal({
  isOpen,
  productName,
  onConfirm,
  onCancel,
  isDeleting,
}: DeleteConfirmModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isDeleting) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isDeleting, onCancel]);

  // Trap focus in modal
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={!isDeleting ? onCancel : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
        className="relative z-10 mx-4 w-full max-w-md overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border bg-destructive/5 px-6 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
            <TrashBinTrash size={20} className="text-destructive" />
          </div>
          <h2 id="delete-modal-title" className="text-lg font-semibold text-foreground">
            Confirmar exclusão
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <p id="delete-modal-description" className="text-sm text-muted-foreground">
            Tem certeza que deseja excluir{' '}
            <strong className="text-foreground">"{productName}"</strong>?
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Esta ação não pode ser desfeita. O produto será removido permanentemente do catálogo.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse gap-2 border-t border-border bg-muted/30 px-6 py-4 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isDeleting}
            className="w-full sm:w-auto"
          >
            <CloseCircle size={16} className="mr-2" />
            Cancelar
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={onConfirm}
            disabled={isDeleting}
            className="w-full sm:w-auto"
          >
            {isDeleting ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Excluindo...
              </>
            ) : (
              <>
                <TrashBinTrash size={16} className="mr-2" />
                Excluir
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
